import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    HeartHandshake,
    LockKeyhole,
    Search,
    ShieldCheck,
    Sparkles,
} from 'lucide-react';
import API from '../../utils/api';
import { useOptions } from '../../hooks/useOptions';
import Select from '../ui/Select';
import { responsiveImage } from '../../utils/responsiveImage';
import './Hero.css';

// Religion → hero banner config
const RELIGION_BANNER = {
    hindu: {
        src: '/images/matrimony-hero.png',
        alt: 'Tamil Hindu bride and groom in a warm wedding setting',
        eyebrow: 'Your journey, thoughtfully continued',
    },
    christian: {
        src: '/images/christian-hero.jpg',
        alt: 'Christian bride and groom in an elegant church wedding',
        eyebrow: 'Blessed beginnings, beautifully matched',
    },
    muslim: {
        src: '/images/muslim-hero.jpg',
        alt: 'Muslim bride and groom at a beautiful nikah ceremony',
        eyebrow: 'A blessed union, thoughtfully guided',
    },
};
const DEFAULT_BANNER = {
    src: '/images/matrimony-hero.png',
    alt: 'Tamil bride and groom in a warm wedding setting',
    eyebrow: 'Tamil hearts, thoughtfully matched',
};

const Hero = ({ onRegisterClick, onLoginClick, user = null }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState({ name: '', memberId: '', religion: '' });
    const { options: religionOptions } = useOptions('religion');
    const [profilePersonName, setProfilePersonName] = useState(
        (user?.profileFor && user?.profileFor !== 'Myself' ? user?.profileName : null) || ''
    );

    useEffect(() => {
        if (!user || user.role !== 'member') {
            setProfilePersonName('');
            return;
        }

        const known = (user.profileFor && user.profileFor !== 'Myself' ? user.profileName : null);
        if (known) {
            setProfilePersonName(known);
            return;
        }

        // If user profileFor is for someone else (Son, Brother, Daughter, etc.)
        // but profileName wasn't cached in the user session, fetch /profiles/my
        if (user.profileFor && user.profileFor !== 'Myself' && !user.profileName) {
            API.get('/profiles/my')
                .then(res => {
                    const p = res.data?.profile;
                    if (p && (p.name || p.profileName)) {
                        setProfilePersonName(p.name || p.profileName);
                    }
                })
                .catch(() => {});
        }
    }, [user]);

    const candidateName = profilePersonName
        || (user?.profileFor && user?.profileFor !== 'Myself' ? user?.profileName : null)
        || user?.profileName
        || user?.name
        || 'member';

    const handleSearch = (event) => {
        event.preventDefault();
        const params = new URLSearchParams();
        const name = search.name.trim();
        const memberId = search.memberId.trim().replace(/^#/, '');
        if (memberId) {
            params.set('memberId', memberId);
        } else {
            if (name) params.set('name', name);
            if (search.religion) params.set('religion', search.religion);
        }
        navigate(`/browse${params.size ? `?${params.toString()}` : ''}`);
    };

    // Pick banner based on logged-in user's religion (case-insensitive)
    const religionKey = String(user?.religion || '').trim().toLowerCase();
    const banner = (user && RELIGION_BANNER[religionKey]) || DEFAULT_BANNER;

    return (
        <section className="home-hero" aria-labelledby="home-hero-title">
            <img
                className="home-hero__image"
                fetchPriority="high"
                decoding="async"
                {...responsiveImage(banner.src)}
                alt={banner.alt}
                key={banner.src}
            />
            <div className="home-hero__wash" />
            <div className="home-petals" aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>

            <div className="home-hero__inner">
                <div className="home-hero__copy">
                    <div className="home-hero__eyebrow"><Sparkles size={15} /> {user ? banner.eyebrow : DEFAULT_BANNER.eyebrow}</div>
                    <h1 id="home-hero-title">
                        {user ? <>Welcome, <em>{candidateName}.</em></> : <>Tamil Matrimony — Meaningful Connections on <em>Gettimelam.</em></>}
                    </h1>
                    <p>
                        {user
                            ? 'Continue discovering verified profiles, meaningful connections, and trusted wedding services in one place.'
                            : 'Discover 100% verified Tamil bride and groom profiles from families who share your traditions, community values, and aspirations across Tamil Nadu and worldwide.'}
                    </p>

                    <div className="home-hero__actions">
                        {user ? (
                            <>
                                <button type="button" className="home-hero__primary" onClick={() => navigate('/browse')}>
                                    Browse your matches <ArrowRight size={17} />
                                </button>
                                <button type="button" className="home-hero__text-button" onClick={() => navigate('/dashboard')}>
                                    Open your <strong>Dashboard</strong>
                                </button>
                            </>
                        ) : (
                            <>
                                <button type="button" className="home-hero__primary" onClick={onRegisterClick}>
                                    Create free profile <ArrowRight size={17} />
                                </button>
                                <button type="button" className="home-hero__text-button" onClick={onLoginClick}>
                                    Already a member? <strong>Log in</strong>
                                </button>
                            </>
                        )}
                    </div>

                    <div className="home-hero__trust" aria-label="Gettimelam benefits">
                        <span><ShieldCheck size={16} /> Verified profiles</span>
                        <span><LockKeyhole size={16} /> Privacy first</span>
                        <span><HeartHandshake size={16} /> Family trusted</span>
                    </div>
                </div>

                <form className="home-hero-search" onSubmit={handleSearch} aria-label="Search matrimony profiles">
                <div className="home-hero-search__heading">
                    <span><Search size={17} /></span>
                    <div>
                        <strong>Find your match</strong>
                        <small>Search verified profiles</small>
                    </div>
                </div>

                <label className="home-hero-search__field">
                    <span>Name</span>
                    <input
                        type="search"
                        value={search.name}
                        onChange={event => setSearch(current => ({ ...current, name: event.target.value }))}
                        placeholder="Enter a name"
                        autoComplete="off"
                    />
                </label>

                <label className="home-hero-search__field">
                    <span>Member ID</span>
                    <input
                        type="search"
                        value={search.memberId}
                        onChange={event => setSearch(current => ({ ...current, memberId: event.target.value }))}
                        placeholder="e.g. GM0001"
                        autoComplete="off"
                    />
                </label>

                <div className="home-hero-search__field">
                    <span>Religion</span>
                    <Select
                        className="home-hero-search__religion"
                        value={search.religion}
                        onChange={religion => setSearch(current => ({ ...current, religion }))}
                        options={religionOptions}
                        placeholder="All religions"
                        ariaLabel="Religion"
                        searchable={false}
                        menuMinWidth={220}
                    />
                </div>

                <button type="submit" className="home-hero-search__submit">
                    <Search size={17} /> Search
                </button>
                </form>
            </div>
        </section>
    );
};

export default Hero;
